import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { ProfessionalFiscalCard } from "@/components/shared/ProfessionalFiscalCard";
import { ProfessionalTotpCard } from "@/components/shared/ProfessionalTotpCard";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserAvatar } from "@/components/ui/user-avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePagination } from "@/hooks/usePagination";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getAccountantDetail,
  type AccountantRequestHistoryItem,
} from "@/services/accountantsService";
import type { NotarialRequestStatus } from "@/services/notaryRequestsService";

function ProfileRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): JSX.Element {
  const display = value != null && String(value).trim() !== "" ? String(value) : "—";
  return (
    <div className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-2 first:pt-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{display}</dd>
    </div>
  );
}

function statusVariant(status: string): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "active":
      return "success";
    case "disabled":
      return "danger";
    default:
      return "info";
  }
}

const HISTORY_STATUS_MAP: Record<
  NotarialRequestStatus,
  { labelKey: string; variant: StatusBadgeVariant }
> = {
  pending_profile_completion: {
    labelKey: "notary.requests.statusPendingProfileCompletion",
    variant: "warning",
  },
  // Bank transfer awaiting verification. Professionals never see these rows;
  // the entry exists so the map stays exhaustive over the status union.
  pending_payment_verification: {
    labelKey: "requests.user.statusPendingPaymentVerification",
    variant: "warning",
  },
  pending: { labelKey: "notary.requests.statusPending", variant: "warning" },
  in_progress: { labelKey: "notary.requests.statusInProgress", variant: "info" },
  completed: { labelKey: "notary.requests.statusCompleted", variant: "success" },
  missing_information: {
    labelKey: "notary.requests.statusMissingInfo",
    variant: "danger",
  },
  cannot_complete: {
    labelKey: "notary.requests.statusCannotComplete",
    variant: "softDanger",
  },
  document_ready_at_professional: {
    labelKey: "notary.requests.statusDocumentReadyAtProfessional",
    variant: "warning",
  },
  document_at_platform: { labelKey: "notary.requests.statusDocumentAtPlatform", variant: "info" },
  awaiting_consumer_delivery_details: {
    labelKey: "notary.requests.statusAwaitingConsumerDelivery",
    variant: "info",
  },
  awaiting_shipment_to_consumer: {
    labelKey: "notary.requests.statusAwaitingShipment",
    variant: "info",
  },
  in_transit_to_consumer: {
    labelKey: "notary.requests.statusInTransitToConsumer",
    variant: "info",
  },
};

function HistoryStatusBadge({ status }: { status: string }): JSX.Element {
  const { t } = useTranslation();
  const entry = HISTORY_STATUS_MAP[status as NotarialRequestStatus];
  const label = entry ? t(entry.labelKey) : status;
  const variant = entry?.variant ?? "info";
  return <StatusBadge variant={variant}>{label}</StatusBadge>;
}

function getDisplayName(p: {
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string;
}): string {
  const parts = [p.firstName, p.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (p.displayName?.trim()) return p.displayName;
  return p.email;
}

export function AccountantDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    page: historyPage,
    limit: historyLimit,
    setTotal: setHistoryTotal,
    ...historyPaginationRest
  } = usePagination({ initialPage: 1, initialLimit: 10 });

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["platform", "accountant", id, historyPage, historyLimit],
    queryFn: () =>
      getAccountantDetail(id!, {
        requestsPage: historyPage,
        requestsLimit: historyLimit,
      }),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (data?.requestHistory.total != null) {
      setHistoryTotal(data.requestHistory.total);
    }
  }, [data?.requestHistory.total, setHistoryTotal]);

  const historyPagination = {
    page: historyPage,
    limit: historyLimit,
    setTotal: setHistoryTotal,
    ...historyPaginationRest,
  };

  useEffect(() => {
    if (isError) {
      toast.error(t("accountants.detail.loadError"));
    }
  }, [isError, t]);

  if (!id) {
    navigate("/accountants", { replace: true });
    return <></>;
  }

  if (isLoading || !data) {
    return <LoadingPage />;
  }

  const { profile, requestHistory, totp } = data;
  const accountantDisplayName = getDisplayName(profile);

  const openRequest = (item: AccountantRequestHistoryItem): void => {
    navigate(`/requests/${item.id}`);
  };

  return (
    <div className="space-y-4 p-4 md:p-8">
      <PageHeader
        title={
          <span className="flex min-w-0 items-center gap-3">
            <UserAvatar
              size="lg"
              className="shrink-0"
              firstName={profile.firstName}
              lastName={profile.lastName}
              email={profile.email}
              displayName={accountantDisplayName}
              imageSrc={profile.profileImage}
            />
            <span className="min-w-0">{accountantDisplayName}</span>
          </span>
        }
        description={t("accountants.detail.subtitle")}
        onBack={() => navigate("/accountants")}
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-foreground">
            {t("accountants.detail.profileTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <ProfileRow label={t("accountants.columnEmail")} value={profile.email} />
            <ProfileRow
              label={t("accountants.detail.fieldCedula")}
              value={profile.cedula}
            />
            <div className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-2">
              <dt className="text-sm font-medium text-muted-foreground">
                {t("accountants.detail.fieldProfessionalApproval")}
              </dt>
              <dd>
                {profile.accountantActivationStatus === "pending" ||
                profile.accountantActivationStatus === "active" ? (
                  <StatusBadge
                    variant={
                      profile.accountantActivationStatus === "pending"
                        ? "warning"
                        : "success"
                    }
                  >
                    {profile.accountantActivationStatus === "pending"
                      ? t("accountants.detail.approvalStatus_pending")
                      : t("accountants.detail.approvalStatus_active")}
                  </StatusBadge>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </dd>
            </div>
            <div className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-2">
              <dt className="text-sm font-medium text-muted-foreground">
                {t("accountants.columnStatus")}
              </dt>
              <dd>
                <StatusBadge variant={statusVariant(profile.status)}>
                  {t(`users.page.status.${profile.status}`)}
                </StatusBadge>
              </dd>
            </div>
            <ProfileRow
              label={t("accountants.columnCreated")}
              value={formatDisplayDate(profile.createdAt)}
            />
            <ProfileRow
              label={t("accountants.detail.fieldEmailVerified")}
              value={
                profile.emailVerifiedAt != null
                  ? formatDisplayDate(profile.emailVerifiedAt)
                  : null
              }
            />
            <ProfileRow
              label={t("accountants.columnInvitedBy")}
              value={profile.invitedByEmail}
            />
            <ProfileRow
              label={t("accountants.detail.fieldInviteAccepted")}
              value={
                profile.inviteAcceptedAt != null
                  ? formatDisplayDate(profile.inviteAcceptedAt)
                  : null
              }
            />
          </dl>
          {profile.pendingReviewInviteId ? (
            <div className="mt-4 flex flex-wrap justify-end border-t border-border pt-4">
              <Button
                type="button"
                className="transition-colors duration-150"
                onClick={() =>
                  navigate(
                    `/accountants/invites/${profile.pendingReviewInviteId}/review`
                  )
                }
              >
                {t("accountants.detail.reviewFromDetail")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Above the 2FA card: an admin on this page is usually asking why the
          professional cannot be activated, and the fiscal declaration is what
          the activation gate actually reads. */}
      <ProfessionalFiscalCard
        fiscalStatus={profile.fiscalStatus}
        rut={profile.rut}
        razonSocial={profile.razonSocial}
        issuesCfe={profile.issuesCfe}
        cfeProvider={profile.cfeProvider}
      />

      <ProfessionalTotpCard userId={profile.id} totp={totp} onResetComplete={refetch} />

      <div>
        <h2 className="mb-2 text-lg font-medium text-foreground">
          {t("accountants.detail.requestHistoryTitle")}
        </h2>
        <p className="mb-2 text-sm text-muted-foreground">
          {t("accountants.detail.requestHistoryHint")}
        </p>
        <TableCard>
          {requestHistory.items.length === 0 ? (
            <div className="py-12">
              <EmptyState title={t("accountants.detail.historyEmpty")} />
            </div>
          ) : (
            <>
              <ResponsiveListSplit
                table={
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("accountants.detail.colApplicant")}</TableHead>
                        <TableHead>{t("accountants.columnEmail")}</TableHead>
                        <TableHead>{t("accountants.columnStatus")}</TableHead>
                        <TableHead>{t("accountants.detail.colAccepted")}</TableHead>
                        <TableHead>{t("accountants.detail.colUpdated")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requestHistory.items.map((row) => (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          role="button"
                          tabIndex={0}
                          onClick={() => openRequest(row)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openRequest(row);
                            }
                          }}
                        >
                          <TableCell className="font-medium text-foreground">
                            {row.applicantName ?? "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {row.applicantEmail ?? "—"}
                          </TableCell>
                          <TableCell>
                            <HistoryStatusBadge status={row.status} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {row.acceptedAt != null
                              ? formatDisplayDate(row.acceptedAt)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDisplayDate(row.updatedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="divide-y divide-border">
                    {requestHistory.items.map((row) => (
                      <DataListCardRow key={row.id} onClick={() => openRequest(row)}>
                        <p className="font-semibold leading-snug text-foreground">
                          {row.applicantName ?? "—"}
                        </p>
                        <div className="mt-3 space-y-3">
                          <DataListCardField label={t("accountants.columnEmail")}>
                            <span className="text-muted-foreground">
                              {row.applicantEmail ?? "—"}
                            </span>
                          </DataListCardField>
                          <DataListCardField label={t("accountants.columnStatus")}>
                            <HistoryStatusBadge status={row.status} />
                          </DataListCardField>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <DataListCardField label={t("accountants.detail.colAccepted")}>
                              <span className="text-muted-foreground">
                                {row.acceptedAt != null
                                  ? formatDisplayDate(row.acceptedAt)
                                  : "—"}
                              </span>
                            </DataListCardField>
                            <DataListCardField label={t("accountants.detail.colUpdated")}>
                              <span className="text-muted-foreground">
                                {formatDisplayDate(row.updatedAt)}
                              </span>
                            </DataListCardField>
                          </div>
                        </div>
                      </DataListCardRow>
                    ))}
                  </div>
                }
              />
              {requestHistory.total > 0 ? (
                <TablePagination
                  pagination={historyPagination}
                  rowsPerPageLabel={t("accountants.rowsPerPage")}
                  pageOfLabel={t("accountants.pageOf", {
                    from:
                      (historyPagination.page - 1) * historyPagination.limit + 1,
                    to: Math.min(
                      historyPagination.page * historyPagination.limit,
                      historyPagination.total
                    ),
                    total: historyPagination.total,
                  })}
                  onRefresh={() => void refetch()}
                  isRefreshing={isFetching}
                />
              ) : null}
            </>
          )}
        </TableCard>
      </div>
    </div>
  );
}
